import React, { useState, useRef, useEffect } from 'react';
import { Lock, Info, KeyRound } from 'lucide-react';

const TwoFactorFormUI = ({ 
    onSubmit, 
    onCancel, 
    loading = false, 
    error = '', 
    showRememberMe = false,
    rememberDevice = false,
    setRememberDevice = () => {},
    allowRecovery = true,
    customTitle = '',
    customSubtitle = '',
    submitButtonText = 'Verify'
}) => {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [localError, setLocalError] = useState('');
    const inputRefs = useRef([]);

    // Keep local error in sync with prop error
    useEffect(() => {
        setLocalError(error);
    }, [error]);

    useEffect(() => {
        if (!useRecovery && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [useRecovery]);

    const handleCodeChange = (index, value) => {
        const val = value.replace(/\D/g, "").slice(-1);
        if (value && !val) return;
        
        const newCode = [...code];
        newCode[index] = val;
        setCode(newCode);

        // Move to next input
        if (val && index < 5) {
            inputRefs.current[index + 1].focus();
        }
        setLocalError('');
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            e.preventDefault();
            const newCode = [...code];
            newCode[index - 1] = '';
            setCode(newCode);
            setLocalError('');
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
        if (pastedData) {
            const newCode = [...code];
            for (let i = 0; i < pastedData.length; i++) {
                newCode[i] = pastedData[i];
            }
            setCode(newCode);
            const nextIndex = Math.min(pastedData.length, 5);
            inputRefs.current[nextIndex].focus();
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        const finalCode = useRecovery ? recoveryCode.replace(/\s/g, "") : code.join('');
        
        if (!useRecovery && finalCode.length !== 6) {
            setLocalError('Please enter a 6-digit code');
            return;
        }
        if (useRecovery && finalCode.length !== 8) {
            setLocalError('Recovery code must be 8 characters');
            return;
        }

        setLocalError('');
        onSubmit(finalCode, useRecovery);
    };

    const toggleRecovery = () => {
        setUseRecovery(!useRecovery);
        setLocalError('');
        setCode(['', '', '', '', '', '']);
        setRecoveryCode('');
    };

    return (
        <div className="flex flex-col items-center text-center w-full">
            {/* Icon */}
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-2.5 shrink-0 border border-blue-100/60 shadow-sm">
                {useRecovery ? <KeyRound size={20} strokeWidth={2} /> : <Lock size={20} strokeWidth={2} />}
            </div>

            {/* Titles */}
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">
                {customTitle || (useRecovery ? "Use a Recovery Code" : "Verify with Authenticator")}
            </h2>
            <p className="text-xs sm:text-[13px] text-slate-500 mb-3.5 max-w-xs leading-relaxed">
                {customSubtitle || (useRecovery 
                    ? "Enter one of your 8-character recovery codes to verify your identity."
                    : "For your security, please enter the 6-digit code from your authenticator app.")}
            </p>

            <form onSubmit={handleSubmit} className="w-full">
                {/* Inputs */}
                {!useRecovery ? (
                    <div className="flex justify-center gap-1.5 sm:gap-2 mb-3.5" onPaste={handlePaste}>
                        {code.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                inputMode="numeric"
                                maxLength={2}
                                value={digit}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                className="w-9 h-11 sm:w-10 sm:h-12 text-center text-lg sm:text-xl font-bold text-slate-800 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm"
                                placeholder="•"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="mb-3.5">
                        <input 
                            autoFocus
                            type="text"
                            maxLength={8}
                            value={recoveryCode}
                            onChange={(e) => setRecoveryCode(e.target.value)}
                            placeholder="e.g. 1a2b3c4d"
                            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-center text-base sm:text-lg font-bold tracking-widest text-slate-800 uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm"
                        />
                    </div>
                )}

                {localError && (
                    <div className="mb-3 text-xs font-medium text-red-500 bg-red-50 px-2.5 py-1.5 rounded-lg border border-red-100 flex items-center justify-center gap-1.5">
                        <Info size={13} className="shrink-0" />
                        <span>{localError}</span>
                    </div>
                )}

                {/* Remember me */}
                {showRememberMe && (
                    <div className="flex items-center justify-center gap-1.5 mb-3.5">
                        <input 
                            type="checkbox" 
                            id="rememberDevice"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            className="w-3.5 h-3.5 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        <label htmlFor="rememberDevice" className="text-xs font-medium text-slate-600 cursor-pointer flex items-center gap-1">
                            Remember this device for 15 minutes
                            <div className="text-blue-500" title="You won't be asked for a code on this device for 15 minutes">
                                <Info size={13} />
                            </div>
                        </label>
                    </div>
                )}

                {/* Buttons */}
                <div className={`flex items-center gap-2.5 w-full ${!showRememberMe ? 'mt-2' : ''}`}>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="w-full bg-white border border-slate-300 text-slate-700 font-semibold py-2 px-3 rounded-xl hover:bg-slate-50 transition-colors shadow-sm text-xs sm:text-sm"
                        >
                            Cancel
                        </button>
                    )}
                    <button 
                        type="submit"
                        disabled={loading || (!useRecovery && code.join('').length !== 6) || (useRecovery && recoveryCode.length !== 8)}
                        className="w-full bg-[#2563eb] text-white font-semibold py-2 px-3 rounded-xl hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-xs sm:text-sm"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Verifying...
                            </>
                        ) : (
                            submitButtonText
                        )}
                    </button>
                </div>
            </form>

            {/* Toggle (only if allowRecovery is true) */}
            {allowRecovery && (
                <>
                    <div className="mt-3.5 flex items-center w-full">
                        <div className="flex-grow border-t border-slate-100"></div>
                        <span className="flex-shrink-0 mx-3 text-[11px] text-slate-400">
                            {useRecovery ? "Found your authenticator app?" : "Can't access your authenticator app?"}
                        </span>
                        <div className="flex-grow border-t border-slate-100"></div>
                    </div>
                    
                    <button 
                        type="button"
                        onClick={toggleRecovery}
                        className="mt-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                    >
                        {useRecovery ? "Use authenticator app" : "Use a recovery code"}
                    </button>
                </>
            )}
        </div>
    );
};

export default TwoFactorFormUI;
